import { CapabilityArea, CapabilityTier } from '../generated/enums.gen';

// Capability-based permission types

// The API sends a tier as its number (None 0 … Manage 3), not its name.
export { CapabilityArea, CapabilityTier };

// All known areas for iteration (UI rendering, validation)
export const CAPABILITY_AREAS: CapabilityArea[] = ['FieldOps', 'Analytics', 'TeamManagement', 'Finance', 'Fundraising'];

// Area display metadata
export const CAPABILITY_AREA_LABELS: Record<CapabilityArea, string> = {
  FieldOps: 'Field Operations',
  Analytics: 'Analytics',
  TeamManagement: 'Team Management',
  Finance: 'Finance',
  Fundraising: 'Fundraising',
};

export const CAPABILITY_TIER_LABELS: Record<CapabilityTier, string> = {
  [CapabilityTier.None]: 'None',
  [CapabilityTier.View]: 'View',
  [CapabilityTier.Do]: 'Do',
  [CapabilityTier.Manage]: 'Manage',
};

// Capabilities as a flexible record (extensible — new areas are just new keys)
export type UserCapabilities = Partial<Record<CapabilityArea, CapabilityTier>>;

export enum PresetKey {
  FullAdmin = 'FullAdmin',
  FieldDirector = 'FieldDirector',
  Volunteer = 'Volunteer',
  ViewOnly = 'ViewOnly',
}

export interface Preset {
  key: PresetKey;
  label: string;
  description: string;
  capabilities: UserCapabilities;
}

export const PRESETS: Preset[] = [
  {
    key: PresetKey.FullAdmin,
    label: 'Full Admin',
    description: 'Full control over all areas',
    // Mirrors the backend FullAdmin invariant (all areas → Manage, synthesized from the enum), so a
    // FullAdmin user with Fundraising:Manage still resolves to this preset rather than "Custom".
    capabilities: { FieldOps: CapabilityTier.Manage, Analytics: CapabilityTier.Manage, TeamManagement: CapabilityTier.Manage, Finance: CapabilityTier.Manage, Fundraising: CapabilityTier.Manage },
  },
  {
    key: PresetKey.FieldDirector,
    label: 'Field Director',
    description: 'Manages field operations and can invite volunteers',
    capabilities: { FieldOps: CapabilityTier.Manage, Analytics: CapabilityTier.Manage, TeamManagement: CapabilityTier.Do, Finance: CapabilityTier.View },
  },
  {
    key: PresetKey.Volunteer,
    label: 'Volunteer',
    description: 'Works shifts and views their own stats',
    capabilities: { FieldOps: CapabilityTier.Do, Analytics: CapabilityTier.View },
  },
  {
    key: PresetKey.ViewOnly,
    label: 'View Only',
    description: 'Read-only access to field ops and analytics',
    capabilities: { FieldOps: CapabilityTier.View, Analytics: CapabilityTier.View, Finance: CapabilityTier.View },
  },
];

// Helper: check if user capabilities match a preset
export function matchPreset(capabilities: UserCapabilities): PresetKey | null {
  for (const preset of PRESETS) {
    const match = CAPABILITY_AREAS.every(
      (area) => (capabilities[area] ?? CapabilityTier.None) === (preset.capabilities[area] ?? CapabilityTier.None)
    );
    if (match) return preset.key;
  }
  return null;
}

// Helper: resolve a preset to capabilities
export function resolvePreset(key: PresetKey): UserCapabilities {
  const preset = PRESETS.find((p) => p.key === key);
  if (!preset) throw new Error(`Unknown preset: ${key}`);
  return { ...preset.capabilities };
}
